import React, { useEffect, useMemo, useState } from 'react';
import { X, Lock, AlertTriangle } from 'lucide-react';
import { api, otpService } from '../../../services/api';
import { API_ENDPOINTS } from '../../../config/api';
import { getAdminMobile } from '../../../utils/session';
import Snackbar from '../../common/Snackbar';

export interface LockedRecipient {
    mobile: string;
    name?: string | null;
    amount: number;
    is_direct: boolean;
    // 'LOCKED' rewards are the ones this release pays; 'AVAILABLE' ones are
    // already in the wallet and are listed only so a side never looks absent.
    status: 'LOCKED' | 'AVAILABLE' | string;
}

export interface Referrer {
    mobile: string;
    name?: string | null;
}

export interface LockedOrder {
    orderId: string;
    numUnits?: number | null;
    buyerMobile?: string | null;
    buyerName?: string | null;
    cancelled?: boolean;
    direct_locked_coins: number;
    indirect_locked_coins: number;
    total_locked_coins: number;
    recipients: LockedRecipient[];
    // Who the referral chain says would be paid, used when a side has no reward
    // row at all — it separates "never credited" from "nobody upstream".
    direct_referrer?: Referrer | null;
    indirect_referrer?: Referrer | null;
}

interface Props {
    isOpen: boolean;
    mobile: string;
    userName?: string;
    onClose: () => void;
    onReleased: (message: string) => void;
}

const inr = (n: number) => (n || 0).toLocaleString('en-IN');

/**
 * One side (direct or indirect) of an order's referral rewards.
 *
 * Renders a row per reward, marking which are LOCKED (this release pays them)
 * and which are already in the wallet. When there is no reward at all it says
 * WHICH of the two situations that is: a referrer exists but was never
 * credited, or there is nobody at that level of the chain. An empty line would
 * conflate them, and the two mean very different things to an admin about to
 * release money.
 */
const RewardSide: React.FC<{
    label: 'Direct' | 'Indirect';
    rows: LockedRecipient[];
    referrer?: Referrer | null;
}> = ({ label, rows, referrer }) => {
    if (rows.length === 0) {
        return (
            <p className="text-[10px] text-slate-400 italic">
                {label}: {referrer
                    ? `${referrer.name || referrer.mobile} — no reward recorded`
                    : `no ${label.toLowerCase()} referrer`}
            </p>
        );
    }
    return (
        <>
            {rows.map((r) => (
                <p
                    key={`${label}-${r.mobile}-${r.amount}`}
                    className="text-[10px] text-slate-600 flex items-center gap-1.5 flex-wrap"
                >
                    <span className="font-bold text-slate-500">{label}:</span>
                    <span className="font-semibold">{r.name || r.mobile}</span>
                    <span className="font-black text-slate-700">{inr(r.amount)}</span>
                    {r.status === 'LOCKED' ? (
                        <span className="px-1.5 py-px rounded-full bg-amber-100 text-amber-700 font-black text-[9px] uppercase tracking-wide">
                            Locked
                        </span>
                    ) : (
                        <span className="px-1.5 py-px rounded-full bg-emerald-50 text-emerald-600 font-black text-[9px] uppercase tracking-wide">
                            Already released
                        </span>
                    )}
                </p>
            ))}
        </>
    );
};

/**
 * Releases a user's locked referral coins ahead of procurement, on Admin/SuperAdmin
 * authority. Self-contained modal (header bar + boxed sections + inline OTP),
 * matching the same shell used by the Add New Self Benefit form.
 */
const UnlockCoinsModal: React.FC<Props> = ({ isOpen, mobile, userName, onClose, onReleased }) => {
    const [orders, setOrders] = useState<LockedOrder[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [adminMobile, setAdminMobile] = useState('');
    const [adminOtp, setAdminOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setReason('');
        setLoadError(null);
        setLoading(true);
        setAdminMobile(getAdminMobile());
        setAdminOtp('');
        setOtpSent(false);
        api.get<any>(API_ENDPOINTS.getLockedCoinOrders(mobile))
            .then((res) => {
                const data = res.data || {};
                const list: LockedOrder[] = data.orders || [];
                setOrders(list);
                // Pre-select everything releasable; a cancelled order's rewards are
                // closed by design and the API refuses them, so they stay unticked.
                setSelected(
                    list.reduce((acc, o) => {
                        acc[o.orderId] = !o.cancelled;
                        return acc;
                    }, {} as Record<string, boolean>),
                );
            })
            .catch((e: any) => setLoadError(e?.message || 'Failed to load locked orders'))
            .finally(() => setLoading(false));
    }, [isOpen, mobile]);

    const chosen = useMemo(
        () => orders.filter((o) => selected[o.orderId] && !o.cancelled),
        [orders, selected],
    );
    const chosenTotal = useMemo(
        () => chosen.reduce((sum, o) => sum + (o.total_locked_coins || 0), 0),
        [chosen],
    );
    const canVerify = chosen.length > 0 && reason.trim().length >= 3;

    const handleSendOtp = async () => {
        if (!adminMobile || adminMobile.length < 10) {
            setSnackbar({ message: 'Please enter a valid mobile number', type: 'error' });
            return;
        }

        setSendingOtp(true);
        try {
            const result = await otpService.sendOtp(adminMobile);
            if (result.error) {
                setSnackbar({ message: result.error, type: 'error' });
            } else {
                setOtpSent(true);
                setSnackbar({ message: 'OTP sent successfully to WhatsApp!', type: 'success' });
            }
        } catch (error) {
            setSnackbar({ message: 'Failed to send OTP', type: 'error' });
        } finally {
            setSendingOtp(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!canVerify) {
            setSnackbar({ message: 'Select at least one order and enter a reason', type: 'error' });
            return;
        }

        if (!otpSent || adminOtp.length < 4) {
            setSnackbar({ message: 'Please request and enter a valid OTP', type: 'error' });
            return;
        }

        setVerifying(true);
        try {
            const res = await api.post<any>(
                API_ENDPOINTS.releaseLockedCoins(),
                { order_ids: chosen.map((o) => o.orderId), reason: reason.trim() },
                { headers: { 'X-Admin-Mobile': adminMobile, 'X-Admin-OTP': adminOtp } },
            );
            const data = res.data || {};
            // A 200 body can still carry a failure (the endpoint reports errors in
            // the payload), so don't announce success on the status code alone.
            if (data.statuscode && data.statuscode !== 200) {
                throw new Error(data.message || 'Release failed');
            }
            onReleased(data.message || 'Locked coins released');
            onClose();
        } catch (error: any) {
            setSnackbar({ message: error.message || 'An unexpected error occurred', type: 'error' });
        } finally {
            setVerifying(false);
        }
    };

    if (!isOpen) return null;

    const labelClasses = 'mb-1.5 block text-sm font-medium text-gray-700';
    const inputClasses = 'h-11 w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:border-[#1B4332]';

    return (
        <>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden transform transition-all animate-scaleIn border border-white/20 max-h-[92vh] flex flex-col">
                    {/* Header — kept as its own separate bar */}
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-4 flex items-center justify-between text-white shrink-0">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-md">
                                <Lock size={18} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold">Release Locked Coins</h3>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-white/10 rounded-lg transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
                        {/* Locked Orders — its own separate square section */}
                        <div className="p-4 bg-white rounded-xl border border-gray-200">
                            <div className="border-b border-gray-200 pb-2 mb-4">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Locked Orders</span>
                            </div>

                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <Lock size={13} className="text-amber-600 shrink-0" />
                                    <span className="text-[11px] font-black text-amber-800 uppercase tracking-wider">
                                        {userName || mobile}
                                    </span>
                                </div>
                                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                                    Releasing an order credits <strong>both</strong> its direct and indirect
                                    rewards to their referrers immediately, without waiting for procurement.
                                </p>
                            </div>

                            {loading && <p className="text-xs text-slate-500 font-semibold">Loading locked orders…</p>}
                            {loadError && <p className="text-xs text-rose-600 font-semibold">{loadError}</p>}
                            {!loading && !loadError && orders.length === 0 && (
                                <p className="text-xs text-slate-500 font-semibold">
                                    No locked referral coins for this user.
                                </p>
                            )}

                            <div className="space-y-2">
                                {orders.map((o) => (
                                    <label
                                        key={o.orderId}
                                        className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                                            o.cancelled
                                                ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                                                : 'border-slate-200 bg-white hover:border-emerald-300 cursor-pointer'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 shrink-0 accent-emerald-600"
                                            disabled={!!o.cancelled}
                                            checked={!!selected[o.orderId] && !o.cancelled}
                                            onChange={(e) =>
                                                setSelected((prev) => ({ ...prev, [o.orderId]: e.target.checked }))
                                            }
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span className="text-xs font-black text-slate-800 truncate">{o.orderId}</span>
                                                <span className="text-xs font-black text-amber-700 whitespace-nowrap">
                                                    {inr(o.total_locked_coins)}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                                                {o.buyerName || o.buyerMobile || 'Unknown buyer'}
                                                {o.numUnits ? ` · ${o.numUnits} unit(s)` : ''}
                                                {' · '}D {inr(o.direct_locked_coins)} · I {inr(o.indirect_locked_coins)}
                                            </p>
                                            {/* Both sides are always shown. An admin approving a payout
                                                must see every recipient — and, where a side has no
                                                reward, be told which reason applies rather than left
                                                looking at a gap. */}
                                            <div className="mt-1.5 space-y-1">
                                                <RewardSide
                                                    label="Direct"
                                                    rows={o.recipients.filter((r) => r.is_direct)}
                                                    referrer={o.direct_referrer}
                                                />
                                                <RewardSide
                                                    label="Indirect"
                                                    rows={o.recipients.filter((r) => !r.is_direct)}
                                                    referrer={o.indirect_referrer}
                                                />
                                            </div>
                                            {o.cancelled && (
                                                <p className="text-[10px] text-rose-600 font-bold mt-1 flex items-center gap-1">
                                                    <AlertTriangle size={10} /> Order cancelled — rewards closed
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>

                            <div className="mt-4">
                                <label className={labelClasses}>Reason (stored on every row released)</label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={2}
                                    placeholder="Why are these coins being released early?"
                                    className={`${inputClasses} h-auto py-2.5 resize-none`}
                                />
                            </div>

                            {chosen.length > 0 && (
                                <p className="text-xs font-black text-slate-700 mt-3">
                                    Releasing {inr(chosenTotal)} coins across {chosen.length} order(s)
                                </p>
                            )}
                        </div>

                        {/* Super Admin Authorization — its own separate square section */}
                        <div className="p-4 bg-white rounded-xl border border-gray-200 space-y-4">
                            <div className="border-b border-gray-200 pb-2">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Super Admin Authorization</span>
                            </div>

                            <div>
                                <label className={labelClasses}>Super Admin Mobile</label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                        type="text"
                                        value={adminMobile}
                                        placeholder="Admin Mobile Number"
                                        className={`${inputClasses} flex-1 min-w-0 bg-gray-100 text-gray-500 opacity-70 cursor-not-allowed`}
                                        disabled
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSendOtp}
                                        disabled={sendingOtp || otpSent || !adminMobile}
                                        className="px-4 h-11 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 whitespace-nowrap shrink-0 w-full sm:w-auto"
                                    >
                                        {sendingOtp ? 'Sending…' : otpSent ? 'Sent' : 'Send OTP'}
                                    </button>
                                </div>
                            </div>

                            {otpSent && (
                                <div className="animate-slideDown">
                                    <label className={labelClasses}>WhatsApp OTP</label>
                                    <input
                                        type="text"
                                        value={adminOtp}
                                        onChange={(e) => setAdminOtp(e.target.value)}
                                        placeholder="Enter 6-digit OTP"
                                        maxLength={6}
                                        className={`${inputClasses} tracking-[0.5em] text-center font-semibold`}
                                    />
                                </div>
                            )}
                        </div>
                      </div>

                        {/* Actions */}
                        <div className="shrink-0 flex gap-3 px-5 py-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={verifying || !otpSent || adminOtp.length < 4 || !canVerify}
                                className="flex-[1.5] px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm shadow-sm active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {verifying ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    'Release Coins'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}
        </>
    );
};

export default UnlockCoinsModal;
